import { useEffect, useRef, useState } from 'react'
import { ACKNOWLEDGEMENTS } from '../data/acknowledgements'

// Required acknowledgements at first sign-in (and whenever a document version changes).
// Each document must be scrolled to the end, confirmed and signed by typing the learner's full name.
// Used by CrossReg Training (default documents/endpoint) and by CrossRegGuard (its own documents/endpoint).
export default function AcknowledgementGate({ user, onComplete, onDecline, documents = ACKNOWLEDGEMENTS, endpoint = '/api/training/acknowledgements', productName = 'CrossReg Training', authHeaders = {} }) {
  const [pending, setPending] = useState(null)   // documents still to acknowledge
  const [index, setIndex] = useState(0)
  const [readToEnd, setReadToEnd] = useState(false)
  const [agreed, setAgreed] = useState(false)
  const [signature, setSignature] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const bodyRef = useRef(null)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const r = await fetch(endpoint, { credentials: 'include', headers: authHeaders })
        const data = r.ok ? await r.json() : { pending: documents.map(a => a.id) }
        const todo = documents.filter(a => (data.pending || []).includes(a.id))
        if (cancelled) return
        if (!todo.length) onComplete({ hadPending: false })
        else setPending(todo)
      } catch {
        if (!cancelled) setPending(documents)
      }
    })()
    return () => { cancelled = true }
  }, [])

  const doc = pending?.[index]

  // Reset per document; short documents that need no scrolling count as read
  useEffect(() => {
    setReadToEnd(false); setAgreed(false); setError(null)
    const el = bodyRef.current
    if (el) { el.scrollTop = 0; requestAnimationFrame(() => { if (el.scrollHeight <= el.clientHeight + 8) setReadToEnd(true) }) }
  }, [doc?.id])

  const onScroll = (e) => {
    const el = e.currentTarget
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 24) setReadToEnd(true)
  }

  const expectedName = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim()
  const signatureOk = signature.trim().length >= 3 && /\s/.test(signature.trim())

  const accept = async () => {
    if (!doc || !readToEnd || !agreed || !signatureOk) return
    setSaving(true); setError(null)
    try {
      const r = await fetch(endpoint, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ documentId: doc.id, version: doc.version, signature: signature.trim() }),
      })
      if (!r.ok) {
        const e = await r.json().catch(() => ({}))
        throw new Error(e.error || 'Could not record your acknowledgement')
      }
      if (index + 1 < pending.length) setIndex(index + 1)
      else onComplete({ hadPending: true })
    } catch (err) {
      setError(err.message)
    } finally {
      setSaving(false)
    }
  }

  if (!pending) {
    return <div className="loading-container"><div className="spinner"></div><p>Loading…</p></div>
  }
  if (!doc) return null

  const btn = (enabled, primary) => ({
    padding: '10px 20px', borderRadius: 6, border: primary ? 'none' : '1px solid #cbd5e1', fontWeight: 600, fontSize: 14,
    background: primary ? (enabled ? '#0A2342' : '#94a3b8') : '#fff', color: primary ? '#fff' : '#334155',
    cursor: enabled ? 'pointer' : 'not-allowed',
  })

  return (
    <div style={{ minHeight: '100vh', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, boxSizing: 'border-box' }}>
      <div role="dialog" aria-labelledby="ack-title" style={{ background: '#fff', borderRadius: 12, boxShadow: '0 10px 40px rgba(15,23,42,0.15)', width: '100%', maxWidth: 820, display: 'flex', flexDirection: 'column', maxHeight: 'calc(100vh - 32px)' }}>
        <div style={{ padding: '20px 28px 12px', borderBottom: '1px solid #e2e8f0' }}>
          <div style={{ fontSize: 11, letterSpacing: '0.18em', color: '#0891b2', fontWeight: 700 }}>
            REQUIRED ACKNOWLEDGEMENT {index + 1} OF {pending.length}
          </div>
          <h1 id="ack-title" style={{ margin: '6px 0 2px', fontSize: 22, color: '#0A2342' }}>{doc.title}</h1>
          <div style={{ fontSize: 12, color: '#64748b' }}>{doc.subtitle} · Version {doc.version}</div>
        </div>

        <div ref={bodyRef} onScroll={onScroll} style={{ padding: '16px 28px', overflowY: 'auto', flex: 1, fontSize: 14, lineHeight: 1.6, color: '#1e293b' }}>
          {doc.sections.map(s => (
            <section key={s.heading} style={{ marginBottom: 18 }}>
              <h2 style={{ fontSize: 15, margin: '0 0 6px', color: '#0A2342' }}>{s.heading}</h2>
              {s.body.map((p, i) => <p key={i} style={{ margin: '0 0 8px' }}>{p}</p>)}
            </section>
          ))}
          <p style={{ fontSize: 12, color: '#64748b', margin: '8px 0 0' }}>© {new Date().getFullYear()} CrossReg Nexus Consultants LLC. All rights reserved.</p>
        </div>

        <div style={{ padding: '14px 28px 20px', borderTop: '1px solid #e2e8f0', background: '#f8fafc', borderRadius: '0 0 12px 12px' }}>
          {!readToEnd && <p style={{ margin: '0 0 10px', fontSize: 12, color: '#b45309' }}>Scroll to the end of the document to continue.</p>}
          <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 13, color: readToEnd ? '#1e293b' : '#94a3b8', marginBottom: 12 }}>
            <input type="checkbox" checked={agreed} disabled={!readToEnd} onChange={e => setAgreed(e.target.checked)} style={{ marginTop: 3 }} />
            <span>{doc.statement}</span>
          </label>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <label style={{ flex: '1 1 260px', fontSize: 12, color: '#475569' }}>
              Type your full name to sign{expectedName ? ` (${expectedName})` : ''}
              <input value={signature} onChange={e => setSignature(e.target.value)} disabled={!readToEnd} placeholder="Full name"
                style={{ display: 'block', width: '100%', boxSizing: 'border-box', marginTop: 4, padding: '9px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 14 }} />
            </label>
            <button style={btn(true, false)} onClick={onDecline}>Decline and sign out</button>
            <button style={btn(readToEnd && agreed && signatureOk && !saving, true)} disabled={!readToEnd || !agreed || !signatureOk || saving} onClick={accept}>
              {saving ? 'Saving…' : index + 1 < pending.length ? 'I acknowledge — continue' : 'I acknowledge'}
            </button>
          </div>
          {error && <p role="alert" style={{ color: '#b91c1c', fontSize: 13, margin: '10px 0 0' }}>{error}</p>}
          <p style={{ fontSize: 11, color: '#64748b', margin: '10px 0 0' }}>Your acknowledgement, typed signature, date and time are recorded. You can't use {productName} until every document is acknowledged.</p>
        </div>
      </div>
    </div>
  )
}
